import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { SettingsAPI, StaffMember } from "../../../services/settings.service";
import { useAddDoctor } from "../../../contexts/AddDoctorContext";
import { useRole } from "../../../hooks/useRole";
import { RolesAPI, Role as RbacRole } from "../../../services/roles.service";
import { actionErrorMessage } from "../../../utils/network";
import { isOwnEmail, OWN_EMAIL_MESSAGE } from "../../../utils/own-account";

// Roles the backend users.role enum accepts. The clinic staff table may show
// finer-grained labels, but only these three can be persisted today.
const ROLES = ["Doctor", "Receptionist", "Admin"] as const;
type Role = (typeof ROLES)[number];

interface Props {
  isOpen:    boolean;
  onClose:   () => void;
  onSuccess: (member: StaffMember) => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const AddStaffModal: React.FC<Props> = ({ isOpen, onClose, onSuccess }) => {
  const navigate = useNavigate();
  const { openAddDoctorModal } = useAddDoctor();
  // Adding a doctor hands off to Doctor Management's Add Doctor flow, which is
  // Manage Doctors (POST /doctors) — a separate grant from adding staff here.
  const { canOr, isAdmin } = useRole();
  const canAddDoctor = canOr("doctors.manage", isAdmin);

  const [form, setForm]       = useState({ first_name: "", last_name: "", email: "", role: "Receptionist" as Role });
  const [customRoleId, setCustomRoleId] = useState("");
  const [customRoles, setCustomRoles]   = useState<RbacRole[]>([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors]   = useState<{ first_name?: string; email?: string }>({});

  // Granular roles live in the RBAC catalog (clinic-scoped, not the users.role
  // enum above) — offer the clinic's own clones so both can be set in one go
  // instead of a second trip through Access Control & Permissions.
  useEffect(() => {
    if (!isOpen) return;
    RolesAPI.listRoles()
      .then(roles => setCustomRoles(roles.filter(r => !r.is_system)))
      .catch(() => toast.error("Failed to load custom roles."));
  }, [isOpen]);

  if (!isOpen) return null;

  const reset = () => {
    setForm({ first_name: "", last_name: "", email: "", role: "Receptionist" });
    setCustomRoleId("");
    setErrors({});
  };

  const close = () => { reset(); onClose(); };

  const validate = () => {
    const e: typeof errors = {};
    if (!form.first_name.trim())          e.first_name = "First name is required";
    if (!form.email.trim())               e.email = "Email is required";
    else if (!EMAIL_RE.test(form.email))  e.email = "Enter a valid email";
    return e;
  };

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    const first = form.first_name.trim();
    const last  = form.last_name.trim();
    const email = form.email.trim();
    if (await isOwnEmail(email)) { setErrors({ email: OWN_EMAIL_MESSAGE }); return; }

    // Doctors carry extra details (specialization, department, schedule), so we
    // don't create them here — hand off to the full Add Doctor flow with the
    // name + email pre-filled. Add Doctor does the Cognito provisioning + invite.
    if (form.role === "Doctor") {
      if (!canAddDoctor) {
        toast.error("Adding a doctor needs the \"Manage Doctors\" permission. Ask a clinic admin to add them from Doctor Management.");
        return;
      }
      close();
      navigate("/doctors");
      openAddDoctorModal({ name: `${first} ${last}`.trim(), email });
      return;
    }

    // Receptionist / Admin: provision the login here (same architecture — Cognito
    // invite + temp password), then add to the staff list.
    setLoading(true);
    try {
      let member = await SettingsAPI.addStaff({
        first_name: first,
        last_name:  last,
        email,
        role: form.role,
      });

      // Second call, same as "Manage Roles" does for an existing member — the
      // staff-create endpoint only knows the legacy enum above, not role_ids.
      if (customRoleId) {
        try {
          const roles = await RolesAPI.setStaffRoles(member.user_id, [customRoleId]);
          member = { ...member, roles: roles.map(r => ({ role_id: r.role_id, name: r.name })) };
        } catch (err) {
          // A 403 here is the server refusing a role above the actor's own
          // grants; say so rather than calling it a failure worth retrying.
          toast.error(
            `Staff member added, but the custom role was not assigned: ${actionErrorMessage(
              err, "the request failed.",
            )} Assign it from Access Control & Permissions.`,
          );
        }
      }

      toast.success("Staff member added");
      onSuccess(member);
      close();
    } catch (err: any) {
      toast.error(err?.message || "Failed to add staff member");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Add Staff Member</h2>
            <p className="text-sm text-gray-500">Invite a member to your clinic</p>
          </div>
          <button onClick={close} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">

          {/* First + Last name */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                autoFocus
                className={`w-full border rounded-lg px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 ${errors.first_name ? "border-red-400" : "border-gray-200"}`}
                placeholder="Jane"
                value={form.first_name}
                onChange={e => setForm({ ...form, first_name: e.target.value })}
              />
              {errors.first_name && <p className="text-red-500 text-xs mt-1">{errors.first_name}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
              <input
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="Doe"
                value={form.last_name}
                onChange={e => setForm({ ...form, last_name: e.target.value })}
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              className={`w-full border rounded-lg px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 ${errors.email ? "border-red-400" : "border-gray-200"}`}
              placeholder="jane@clinic.com"
              value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })}
            />
            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
          </div>

          {/* Role */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <select
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-base sm:text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400 cursor-pointer"
              value={form.role}
              onChange={e => setForm({ ...form, role: e.target.value as Role })}
            >
              {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            {form.role === "Doctor" && (
              <p className="text-xs text-gray-400 mt-1">
                You'll be taken to Doctor Management to add specialization, department and schedule.
              </p>
            )}
          </div>

          {/* Custom RBAC role — only meaningful once the user actually exists,
              so hidden for Doctor (created later, in the Add Doctor flow). */}
          {form.role !== "Doctor" && customRoles.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Custom Role (optional)</label>
              <select
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-base sm:text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400 cursor-pointer"
                value={customRoleId}
                onChange={e => setCustomRoleId(e.target.value)}
              >
                <option value="">None</option>
                {customRoles.map(r => <option key={r.role_id} value={r.role_id}>{r.name}</option>)}
              </select>
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
            <button type="button" onClick={close}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={loading}
              className="px-5 py-2 text-sm font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 disabled:opacity-60">
              {loading ? "Adding…" : form.role === "Doctor" ? "Continue" : "Add Staff Member"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddStaffModal;
